import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import moment from "moment";
// moment ships these locales; without them every language shows English day/month names
import "moment/locale/sv";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";

// English
import commonEN from "./locales/en/common.json";
import organizerEN from "./locales/en/organizer.json";
import feedEN from "./locales/en/feed.json";
import tournamentsEN from "./locales/en/tournaments.json";
import playerEN from "./locales/en/player.json";
import notificationsEN from "./locales/en/notifications.json";
import findTournamentEventActionsEN from "./locales/en/findTournamentEventActions.json";
import clubEventActionsEN from "./locales/en/clubEventActions.json";
import clubProfileEN from "./locales/en/clubProfile.json";
import homeEN from "./locales/en/home.json";

// Spanish
import commonES from "./locales/es/common.json";
import organizerES from "./locales/es/organizer.json";
import feedES from "./locales/es/feed.json";
import tournamentsES from "./locales/es/tournaments.json";
import playerES from "./locales/es/player.json";
import notificationsES from "./locales/es/notifications.json";
import findTournamentEventActionsES from "./locales/es/findTournamentEventActions.json";
import clubEventActionsES from "./locales/es/clubEventActions.json";
import clubProfileES from "./locales/es/clubProfile.json";
import homeES from "./locales/es/home.json";

// French
import commonFR from "./locales/fr/common.json";
import organizerFR from "./locales/fr/organizer.json";
import feedFR from "./locales/fr/feed.json";
import tournamentsFR from "./locales/fr/tournaments.json";
import playerFR from "./locales/fr/player.json";
import notificationsFR from "./locales/fr/notifications.json";
import findTournamentEventActionsFR from "./locales/fr/findTournamentEventActions.json";
import clubEventActionsFR from "./locales/fr/clubEventActions.json";
import clubProfileFR from "./locales/fr/clubProfile.json";
import homeFR from "./locales/fr/home.json";

// German
import commonDE from "./locales/de/common.json";
import organizerDE from "./locales/de/organizer.json";
import feedDE from "./locales/de/feed.json";
import tournamentsDE from "./locales/de/tournaments.json";
import playerDE from "./locales/de/player.json";
import notificationsDE from "./locales/de/notifications.json";
import findTournamentEventActionsDE from "./locales/de/findTournamentEventActions.json";
import clubEventActionsDE from "./locales/de/clubEventActions.json";
import clubProfileDE from "./locales/de/clubProfile.json";
import homeDE from "./locales/de/home.json";

// Swedish
import commonSV from "./locales/sv/common.json";
import organizerSV from "./locales/sv/organizer.json";
import feedSV from "./locales/sv/feed.json";
import tournamentsSV from "./locales/sv/tournaments.json";
import playerSV from "./locales/sv/player.json";
import notificationsSV from "./locales/sv/notifications.json";
import findTournamentEventActionsSV from "./locales/sv/findTournamentEventActions.json";
import clubEventActionsSV from "./locales/sv/clubEventActions.json";
import clubProfileSV from "./locales/sv/clubProfile.json";
import homeSV from "./locales/sv/home.json";

// Language priority: URL param (native app) > localStorage > browser detect > default (en)
const SUPPORTED_LANGS = ["en", "es", "fr", "de", "sv"];
// moment / Intl get only bundled codes; anything else is en, never the last imported moment locale
export const dateLocale = (code) => (SUPPORTED_LANGS.includes(code) ? code : "en");

function detectBrowserLang() {
  const browserLangs = navigator.languages || [navigator.language || "en"];
  for (const lang of browserLangs) {
    const base = lang.split("-")[0].toLowerCase();
    if (SUPPORTED_LANGS.includes(base)) return base;
  }
  return "en";
}

const params = new URLSearchParams(window.location.search);
const urlLang = params.get("lang");
const storedLang = localStorage.getItem("scoreholio_lang");
// Only run browser detection on web (no platform param = not a native webview)
const isNative = !!params.get("platform");
const lng = urlLang || storedLang || (!isNative ? detectBrowserLang() : "en");

// Save to localStorage for future visits
if (lng !== storedLang) {
  localStorage.setItem("scoreholio_lang", lng);
}

i18n.use(initReactI18next).init({
  lng,
  fallbackLng: "en",
  ns: [
    "common",
    "organizer",
    "feed",
    "tournaments",
    "player",
    "notifications",
    "findTournamentEventActions",
    "clubEventActions",
    "clubProfile",
    "home",
  ],
  defaultNS: "common",
  resources: {
    en: {
      common: commonEN,
      organizer: organizerEN,
      feed: feedEN,
      tournaments: tournamentsEN,
      player: playerEN,
      notifications: notificationsEN,
      findTournamentEventActions: findTournamentEventActionsEN,
      clubEventActions: clubEventActionsEN,
      clubProfile: clubProfileEN,
      home: homeEN,
    },
    es: {
      common: commonES,
      organizer: organizerES,
      feed: feedES,
      tournaments: tournamentsES,
      player: playerES,
      notifications: notificationsES,
      findTournamentEventActions: findTournamentEventActionsES,
      clubEventActions: clubEventActionsES,
      clubProfile: clubProfileES,
      home: homeES,
    },
    fr: {
      common: commonFR,
      organizer: organizerFR,
      feed: feedFR,
      tournaments: tournamentsFR,
      player: playerFR,
      notifications: notificationsFR,
      findTournamentEventActions: findTournamentEventActionsFR,
      clubEventActions: clubEventActionsFR,
      clubProfile: clubProfileFR,
      home: homeFR,
    },
    de: {
      common: commonDE,
      organizer: organizerDE,
      feed: feedDE,
      tournaments: tournamentsDE,
      player: playerDE,
      notifications: notificationsDE,
      findTournamentEventActions: findTournamentEventActionsDE,
      clubEventActions: clubEventActionsDE,
      clubProfile: clubProfileDE,
      home: homeDE,
    },
    sv: {
      common: commonSV,
      organizer: organizerSV,
      feed: feedSV,
      tournaments: tournamentsSV,
      player: playerSV,
      notifications: notificationsSV,
      findTournamentEventActions: findTournamentEventActionsSV,
      clubEventActions: clubEventActionsSV,
      clubProfile: clubProfileSV,
      home: homeSV,
    },
  },
  interpolation: {
    escapeValue: false, // React already handles escaping
  },
});

// Keep moment's day/month names ("ddd, MMM D" etc.) in the app language
moment.locale(dateLocale(lng));

// Save language to localStorage whenever it changes
i18n.on("languageChanged", (newLng) => {
  localStorage.setItem("scoreholio_lang", newLng);
  moment.locale(dateLocale(newLng));
});

export default i18n;
